import {
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type ReactNode
} from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthChrome } from '../components/auth/AuthChrome';
import {
  Banner,
  Button,
  Card,
  HandlePreview,
  PasswordField,
  PasswordStrengthMeter,
  Select,
  type SelectOption,
  TextField
} from '../components/primitives';
import { supabase } from '../lib/supabase';
import {
  PASSWORD_MIN_LENGTH,
  PASSWORD_MIN_SCORE,
  scorePassword
} from '../lib/passwordScore';
import './SignUp.css';

const FUNCTION_ROLE_OPTIONS: SelectOption[] = [
  { value: 'engineer', label: 'Engineer' },
  { value: 'designer', label: 'Designer' },
  { value: 'product', label: 'Product' },
  { value: 'qa', label: 'QA' },
  { value: 'other', label: 'Other' },
  { value: 'custom', label: 'Custom — set up later in Settings' }
];

const ORG_NAME_PATTERN = /^[A-Za-z0-9 -]{2,40}$/;
const RESERVED_ORG_NAMES = new Set(['penrose', 'admin', 'system', 'support']);
const EMAIL_SHAPE = /.+@.+\..+/;

function useOnlineStatus(): boolean {
  const [online, setOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine
  );
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

type InviteData = { token: string; orgName: string; email: string };

export function SignUp() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const inviteToken = searchParams.get('invite');
  const redirectTo = searchParams.get('redirect_to');
  const ideHandoff = redirectTo?.startsWith('penrose://auth/callback') ?? false;

  // Resolved invite. null = either not yet resolved, no token param, or
  // invalid token (we fall back to the self-serve form in that last case).
  const [invite, setInvite] = useState<InviteData | null>(null);
  const [badToken, setBadToken] = useState(false);

  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [orgName, setOrgName] = useState('');
  const [functionRoleKey, setFunctionRoleKey] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<ReactNode>(null);
  const [sessionChecked, setSessionChecked] = useState(false);

  const online = useOnlineStatus();

  // Already-signed-in fast path + invite resolution.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [{ data: sessionData }, inviteResult] = await Promise.all([
        supabase.auth.getSession(),
        inviteToken
          ? supabase.rpc('resolve_invitation', { p_token: inviteToken })
          : Promise.resolve(null)
      ]);
      if (cancelled) return;

      if (sessionData.session) {
        if (inviteToken) {
          navigate(`/invite?token=${encodeURIComponent(inviteToken)}`, {
            replace: true
          });
          return;
        }
        navigate('/dashboard', { replace: true });
        return;
      }

      if (inviteToken) {
        const row =
          inviteResult && Array.isArray(inviteResult.data)
            ? inviteResult.data[0]
            : null;
        if (row && row.ok) {
          const invEmail = (row.email ?? '').toString();
          setInvite({
            token: inviteToken,
            orgName: (row.org_name ?? '').toString(),
            email: invEmail
          });
          setEmail(invEmail);
        } else {
          setBadToken(true);
        }
      }

      setSessionChecked(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, inviteToken]);

  const invitedMode = invite !== null;
  const showBadTokenBanner = badToken;

  const trimmedEmail = email.trim();
  const trimmedUsername = username.trim();
  const trimmedOrg = orgName.trim();
  const emailLooksValid = !!trimmedEmail && EMAIL_SHAPE.test(trimmedEmail);

  const pwdScore = useMemo(() => scorePassword(password), [password]);
  const pwdLongEnough = password.length >= PASSWORD_MIN_LENGTH;
  const pwdStrongEnough = pwdLongEnough && pwdScore >= PASSWORD_MIN_SCORE;
  const pwdsMismatch = !!confirmPwd && confirmPwd !== password;

  const orgFormatValid = !trimmedOrg || ORG_NAME_PATTERN.test(trimmedOrg);
  const orgReserved = !!trimmedOrg && RESERVED_ORG_NAMES.has(trimmedOrg.toLowerCase());

  const orgError = trimmedOrg && !ORG_NAME_PATTERN.test(trimmedOrg)
    ? 'Letters, numbers, spaces, and dashes only — 2 to 40 characters.'
    : orgReserved
      ? 'That name is reserved. Try a different one.'
      : undefined;

  const orgRequirementsMet = invitedMode
    ? true
    : !!trimmedOrg && orgFormatValid && !orgReserved;
  const functionRoleRequired = !invitedMode;

  const canSubmit =
    online &&
    !submitting &&
    emailLooksValid &&
    !!trimmedUsername &&
    orgRequirementsMet &&
    (!functionRoleRequired || !!functionRoleKey) &&
    pwdStrongEnough &&
    !!confirmPwd &&
    !pwdsMismatch;

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canSubmit) return;
    setServerError(null);
    setSubmitting(true);

    const userMetadata: Record<string, string> = {
      username: trimmedUsername
    };
    if (invite) {
      userMetadata.invitation_token = invite.token;
    } else {
      userMetadata.org_name = trimmedOrg;
      if (functionRoleKey !== 'custom') {
        userMetadata.function_role_key = functionRoleKey;
      }
    }

    try {
      const result = await supabase.auth.signUp({
        email: trimmedEmail,
        password,
        options: { data: userMetadata }
      });
      const { error } = result;

      if (error) {
        // Defensive cleanup for the rare case the trigger failed but left a
        // stranded auth.users row. With the current AFTER INSERT trigger
        // raising an exception this rolls back automatically; this path
        // mostly exists to satisfy the spec contract.
        const orphanUserId = (result.data as { user: { id: string } | null } | null)?.user?.id;
        if (orphanUserId) {
          await supabase.functions
            .invoke('compensating-delete-auth-user', {
              body: { user_id: orphanUserId }
            })
            .catch(() => {});
        }
        const msg = error.message?.toLowerCase() ?? '';
        if (
          msg.includes('already registered') ||
          msg.includes('already been registered') ||
          msg.includes('user already exists')
        ) {
          setServerError(
            <>
              This email already has a Penrose account.{' '}
              <Link to="/sign-in">Sign in instead.</Link>
            </>
          );
        } else {
          setServerError("Couldn't create your account. Please try again.");
        }
        setSubmitting(false);
        return;
      }

      // Success. /invitation-acceptance + IDE deeplink not routed yet — head
      // straight to /dashboard. Email-confirmation banner is the dashboard's
      // job (separate prd item).
      navigate('/dashboard', { replace: true });
    } catch {
      setServerError("We couldn't reach Penrose. Check your connection and try again.");
      setSubmitting(false);
    }
  }

  if (!sessionChecked) return null;

  const passwordHelp = `Minimum ${PASSWORD_MIN_LENGTH} characters. Submit unlocks at "Fair" or stronger.`;

  return (
    <>
      <AuthChrome pageLabel="Sign up" />
      <main className="su-main">
        <Card className="su-card" aria-labelledby="su-heading">
          <h1 id="su-heading" className="su-heading">
            Create your Penrose account
          </h1>

          {invite ? (
            <Banner
              kind="accent"
              title={`You've been invited to ${invite.orgName}`}
            >
              Create your Penrose account to accept. Sent to {invite.email}.
            </Banner>
          ) : null}

          {!invitedMode && ideHandoff ? (
            <Banner kind="accent" title="Signing up for Penrose IDE">
              We'll send you back to your IDE when you're done.
            </Banner>
          ) : null}

          {showBadTokenBanner ? (
            <Banner title="That invite link didn't work">
              It may have expired or already been used.{' '}
              <Link to="/sign-up" replace>
                Sign up without an invite.
              </Link>
            </Banner>
          ) : null}

          {serverError ? <Banner kind="error">{serverError}</Banner> : null}

          <form className="su-form" onSubmit={handleSubmit} noValidate>
            <TextField
              id="su-email"
              label="Email"
              type="email"
              autoComplete="email"
              required
              autoFocus={!invitedMode}
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              readOnly={invitedMode}
              data-locked={invitedMode ? 'true' : undefined}
            />

            <TextField
              id="su-username"
              label="Username"
              type="text"
              autoComplete="username"
              required
              maxLength={32}
              value={username}
              onChange={(e) => setUsername(e.currentTarget.value)}
              trailing={
                <HandlePreview
                  username={trimmedUsername}
                  caption="A 4-digit suffix is assigned automatically. Your full handle is unique even when usernames repeat."
                />
              }
            />

            {!invitedMode ? (
              <TextField
                id="su-org-name"
                label="Org name"
                type="text"
                required
                maxLength={40}
                value={orgName}
                onChange={(e) => setOrgName(e.currentTarget.value)}
                helpText={
                  orgError
                    ? undefined
                    : '2–40 characters. Letters, numbers, spaces, and dashes only. Must be unique.'
                }
                errorText={orgError}
              />
            ) : null}

            {!invitedMode ? (
              <Select
                id="su-function-role"
                label="Your function role"
                options={FUNCTION_ROLE_OPTIONS}
                placeholder="Choose…"
                required
                value={functionRoleKey}
                onChange={(e) => setFunctionRoleKey(e.currentTarget.value)}
                helpText="Shapes how your agent behaves. You can change it later."
              />
            ) : null}

            <PasswordField
              id="su-password"
              label="Password"
              autoComplete="new-password"
              required
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              helpText={passwordHelp}
              trailing={
                <PasswordStrengthMeter score={pwdScore} hasInput={password.length > 0} />
              }
            />

            <PasswordField
              id="su-password-confirm"
              label="Confirm password"
              autoComplete="new-password"
              required
              value={confirmPwd}
              onChange={(e) => setConfirmPwd(e.currentTarget.value)}
              errorText={pwdsMismatch ? "Passwords don't match." : undefined}
            />

            <Button
              type="submit"
              variant="primary"
              size="lg"
              disabled={!canSubmit}
              className="su-submit"
            >
              {submitting ? 'Creating account…' : 'Create account'}
            </Button>

            {!online ? (
              <span className="su-offline" role="status">
                Offline — sign-up requires a connection.
              </span>
            ) : null}
          </form>

          <p className="su-alt">
            Already have an account?{' '}
            <Link
              to={
                invite
                  ? `/sign-in?invite=${encodeURIComponent(invite.token)}`
                  : redirectTo
                    ? `/sign-in?redirect_to=${encodeURIComponent(redirectTo)}`
                    : '/sign-in'
              }
            >
              Sign in
            </Link>
          </p>
        </Card>
      </main>
    </>
  );
}
